import type { HrefTarget } from "../../types/layout";

export type Mode = "page" | "manual" | "external";

export type PageListItem = {
	id?: string | null;
	slug?: string | null;
	language: string;
	title?: string | null;
	metadata?: { description?: string | null } | any;
};

export type HrefInputProps = {
	value?: HrefTarget | null;
	onChange: (next?: HrefTarget | null) => void;
	label?: string;
	helper?: string;
	disabled?: boolean;
	allowPage?: boolean;
	allowExternal?: boolean;
	allowManual?: boolean;
	className?: string;
};

export const DEFAULT_LANGUAGE = "fr";
export const PAGE_SIZE = 30;
export const MOBILE_PREVIEW_WIDTH = 430;

export const CATEGORY_FILTERS = [
	{ key: "all", label: "All", prefixes: [] as string[] },
	{ key: "communiques", label: "Communiqués", prefixes: ["communique/"] },
	{ key: "statistiques", label: "Statistiques", prefixes: ["statistiques/"] },
	{ key: "publications", label: "Publications", prefixes: ["publications/"] },
	{ key: "evenements", label: "Événements", prefixes: ["evenements/"] }
];

export const ensureLeadingSlash = (slug: string) => {
	const normalized = slug.trim();
	if (!normalized) return "";
	return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

export const formatDisplay = (value?: HrefTarget | null) => {
	if (!value) return "No link selected";
	const slug = value.slug?.toString().trim();
	if (value.external) return `External • ${value.external}`;
	if (slug) return `Path • ${ensureLeadingSlash(slug)}`;
	if (value.pageId) return `Page • /page/${value.pageId}`;
	return "No link selected";
};

export const resolveMode = (value: HrefTarget | null | undefined, modes: Mode[]): Mode => {
	if (!modes.length) return "manual";
	if (value?.external && modes.includes("external")) return "external";
	if ((value?.pageId || value?.slug) && modes.includes("page")) return "page";
	// Default to page selection when available; fall back to manual then external.
	if (modes.includes("page")) return "page";
	if (modes.includes("manual")) return "manual";
	return modes[0];
};

export const buildPageHref = (page: PageListItem): HrefTarget => {
	const slug = page.slug ? ensureLeadingSlash(page.slug) : page.id ? `/page/${page.id}` : "";
	return {
		slug: slug || undefined,
		pageId: page.id || undefined
	};
};

export const normalizeComponents = (components?: any[] | null): any[] => {
	if (!Array.isArray(components)) return [];
	return components.map((component, index) => {
		const componentType = (component?.componentType ?? component?.ComponentType ?? "").toString();
		const key = (component?.key ?? component?.Key ?? `${componentType || "component"}-${index + 1}`).toString();
		const content = component?.content ?? component?.Content ?? {};
		return { componentType, key, content };
	});
};
